'use client';

import type { FC } from 'react';
import { Credits } from '../../Credits';
import { OverlayBadge } from '../../ui/OverlayBadge';
import { PrizeCardShell } from '../../PrizeCardShell';
import { PrizeStatTile, PrizeStatTilePair } from '../../PrizeStatTile';
import { FaClock, FaUsers, IoPricetag } from '../../icons';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import { useIsDesktop } from '../../../hooks/useIsDesktop';
import { useCurrentTime } from '../../../hooks/useCurrentTime';
import { formatCountdown } from '../../../i18n/formatCountdown';
import translations from './raffle.translations';
import type { RushRaffleRoundListItem } from '../../../services/generated/Api';

interface RaffleCardProps {
  round: RushRaffleRoundListItem;
  onOpen: (roundId: string) => void;
  onPlay: (ticket: number) => void;
}

export const RaffleCard: FC<RaffleCardProps> = ({ round, onOpen, onPlay }) => {
  const { t } = useLocalTranslation(translations);
  const isDesktop = useIsDesktop();
  const now = useCurrentTime();
  const { prize } = round;

  const openRound = () => onOpen(round.roundId);

  const ticketBadge = (
    <OverlayBadge blur className="gap-1.5 px-2.5 py-1 text-xs font-bold text-white">
      {t('ticketBadgeLabel')} <Credits amount={round.ticket} iconSize={13} className="font-extrabold" />
    </OverlayBadge>
  );
  const timerBadge = (
    <OverlayBadge blur className="gap-1.5 px-2.5 py-1 text-xs font-bold tabular-nums text-white">
      <FaClock className="text-[11px] text-white/70" />
      {formatCountdown(round.endsAt, now)}
    </OverlayBadge>
  );

  const topSlot = isDesktop ? (
    <div className="flex items-center justify-between gap-3">
      {ticketBadge}
      {timerBadge}
    </div>
  ) : (
    <>
      <div className="absolute start-3 top-3 z-10">{ticketBadge}</div>
      <div className="absolute end-3 top-3 z-10">{timerBadge}</div>
    </>
  );

  const statsSlot = (
    <PrizeStatTilePair>
      <PrizeStatTile icon={<IoPricetag className="shrink-0 text-lg text-white/80" />} onClick={openRound}>
        <div className="truncate text-xs text-white/70">{t('yourTicketsLabel')}</div>
        <div className="text-lg font-bold text-white">{round.myTickets}</div>
      </PrizeStatTile>
      <PrizeStatTile icon={<FaUsers className="shrink-0 text-lg text-white/80" />} onClick={openRound}>
        <div className="truncate text-xs text-white/70">{t('participantsLabelTop')}</div>
        <div className="truncate text-lg font-bold text-white">{round.participantsCount}</div>
      </PrizeStatTile>
    </PrizeStatTilePair>
  );

  return (
    <PrizeCardShell
      prize={{
        hashName: prize.hashName,
        name: prize.name,
        imageUrl: prize.imageUrl,
        rarityColor: prize.rarityColor,
      }}
      credits={prize.priceCredits}
      steamPrice={prize.steamPriceUsd}
      topSlot={topSlot}
      statsSlot={statsSlot}
      onImageClick={openRound}
      footerLabel={
        <span className="inline-flex items-center gap-1.5">
          {t('playCta')} <Credits amount={round.ticket} iconSize={15} />
        </span>
      }
      onFooterClick={(e) => {
        e.stopPropagation();
        onPlay(round.ticket);
      }}
    />
  );
};

export default RaffleCard;
